<template>
  <div class="md-layout">
    <CSLoading :is-loading="isLoading"></CSLoading>
    <div class="md-layout-item md-size-100 procurement-list">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <span class="material-symbols-outlined">add_shopping_cart</span>
          </div>
          <h4 class="title">{{ $t("procurement.title") }}</h4>
        </md-card-header>
        <md-card-content>
          <div class="table-sticky">
            <v-server-table :columns="tableColumns" :options="tableOptions" ref="table" class="table-procurement" id="table-procurement">
              <template #h__select>
                <md-checkbox class="row-select-checkbox" v-model="allOnPageSelected"></md-checkbox>
              </template>
              <template #select="{ row }">
                <!-- :key erzwingt eine eigene Checkbox je Produkt. Ohne den Schluessel werden die
                     Instanzen beim Filtern wiederverwendet und behalten ihren alten Haken. -->
                <md-checkbox :key="'cart-' + row.productId" class="row-select-checkbox" v-model="row.checked" @change="toggleCart(row, row.checked)"></md-checkbox>
              </template>
              <template #priority="{ row }">
                <div class="cell-priority">
                  <span class="priority-dot" :class="priorityClass(row.priority)"></span>
                  <span v-if="row.priority">{{ row.priority }}</span>
                </div>
              </template>
              <template #sku="{ row }">
                <div class="cell-sku">
                  <div class="text-nowrap">
                    <b>{{ row.sku }}</b>
                  </div>
                  <!-- nach rechts oeffnen: die SKU-Spalte ist ganz links, lange Produktnamen
                       liefen mit direction=top aus dem Fenster -->
                  <md-tooltip md-direction="right">{{ row.name }}</md-tooltip>
                </div>
              </template>
              <template #stock="{ row }">
                <div class="cell-grouped">
                  <div class="text-nowrap">{{ $t("procurement.table.inStock") }}: {{ row.inStock }}</div>
                  <div class="text-nowrap">{{ $t("procurement.table.inFlow") }}: {{ row.inFlow }}</div>
                  <div class="text-nowrap">
                    <b>{{ $t("procurement.table.totalStock") }}: {{ row.totalStock }}</b>
                  </div>
                </div>
              </template>
              <template #sales="{ row }">
                <!-- Summe aus Lagerabsatz + FBA; Aufteilung im Tooltip -->
                <div class="cell-grouped">
                  <div class="text-nowrap" :title="salesTooltip(row, 30)">30d: {{ row.sales30 + row.fba30 }}</div>
                  <div class="text-nowrap" :title="salesTooltip(row, 90)">90d: {{ row.sales90 + row.fba90 }}</div>
                  <div class="text-nowrap" :title="salesTooltip(row, 365)">365d: {{ row.sales365 + row.fba365 }}</div>
                </div>
              </template>
              <template #chart="{ row }">
                <SalesSparkline :series="row.monthlySales" @click="showChartModal(row)" />
              </template>
              <template #range="{ row }">
                <div class="cell-grouped">
                  <div class="text-nowrap">30d: {{ formatRange(row.range30) }}</div>
                  <div class="text-nowrap">90d: {{ formatRange(row.range90) }}</div>
                  <div class="text-nowrap">365d: {{ formatRange(row.range365) }}</div>
                </div>
              </template>
              <template #moq="{ row }">
                <div class="cell-moq">
                  <div class="moq-eol-info">
                    <div class="text-nowrap">MOQ: {{ row.moq != null ? row.moq : "–" }}</div>
                    <div class="text-nowrap eol-indicator">
                      EOL:
                      <md-icon v-if="row.eol" class="eol-yes">check</md-icon>
                      <md-icon v-else class="eol-no">close</md-icon>
                    </div>
                  </div>
                  <md-button
                    v-if="hasPermission('Write', 'Procurement', 'EolMoq')"
                    class="md-icon-button md-simple eol-moq-edit"
                    @click="openEolMoqModal(row)"
                  >
                    <md-icon>edit</md-icon>
                  </md-button>
                </div>
              </template>
              <template #supplierName="{ row }">
                <a v-if="row.supplierName" href="javascript:void(0)" class="supplier-link" @click="filterBySupplier(row)">{{ row.supplierName }}</a>
              </template>
              <template #recommendation="{ row }">
                <div class="cell-recommendation">
                  <div class="text-nowrap">
                    <b>{{ row.recommendedQuantity != null ? row.recommendedQuantity : "–" }}</b>
                    <span v-if="row.recommendedCbm != null" class="cbm-info"> ({{ row.recommendedCbm | formatUnit("cbm", 2, 2) }})</span>
                  </div>
                  <div class="order-quantity-row">
                    <md-field class="order-quantity-field">
                      <md-input type="number" min="0" step="1" v-model.number="row.orderQuantity" @input="syncRowQuantity(row)"></md-input>
                    </md-field>
                    <span v-if="calcCbm(row) != null" class="cbm-info">{{ calcCbm(row) | formatUnit("cbm", 2, 2) }}</span>
                  </div>
                </div>
              </template>
              <template #prices="{ row }">
                <div class="cell-prices">
                  <div class="text-nowrap">EK: {{ row.lastPurchasePrice != null ? $options.filters.formatUnit(row.lastPurchasePrice, "USD", 2, 2) : "–" }}</div>
                  <div class="text-nowrap">BEP: {{ row.bep != null ? $options.filters.formatUnit(row.bep, "€", 2, 2) : "–" }}</div>
                  <div class="text-nowrap">VK: {{ row.currentSellingPrice != null ? $options.filters.formatUnit(row.currentSellingPrice, "€", 2, 2) : "–" }}</div>
                  <div class="text-nowrap">VK netto: {{ nettoVk(row) != null ? $options.filters.formatUnit(nettoVk(row), "€", 2, 2) : "–" }}</div>
                  <div class="text-nowrap" :class="margeClass(row)">
                    {{ $t("procurement.table.margin") }}: {{ marginEur(row) != null ? $options.filters.formatUnit(marginEur(row), "€", 2, 2) : "–" }}
                  </div>
                  <div class="text-nowrap" :class="margePctClass(row)">
                    {{ $t("procurement.table.margin") }} %: {{ marginPct(row) != null ? marginPct(row).toFixed(1) + " %" : "–" }}
                  </div>
                </div>
              </template>
            </v-server-table>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <!-- Sticky-Dock rechts: Bestellen, Suche, Filter (wie im Lieferplan) -->
    <div class="sp-dock">
      <a
        v-if="hasPermission('Write', 'Procurement', 'Order')"
        class="sp-dock-trigger"
        :class="{ 'sp-dock-trigger--disabled': selectedCount === 0 }"
        :title="$t('procurement.order.button')"
        @click="onOrderClick"
      >
        <md-icon>shopping_cart</md-icon>
        <span v-if="selectedCount" class="sp-dock-badge">{{ selectedCount }}</span>
      </a>
      <div class="sp-dock-search">
        <a class="sp-dock-trigger" :class="{ 'sp-dock-trigger--active': filters.keywords || filters.sku }" @click="toggleDockSearch">
          <md-icon>search</md-icon>
        </a>
        <div v-show="dockSearchOpen" class="sp-dock-search-panel">
          <md-field md-clearable>
            <label>{{ $t("procurement.filters.keywords") }}</label>
            <md-input ref="dockSearchInput" v-model="filters.keywords" @input="debounceApplyFilters"></md-input>
          </md-field>
          <md-field md-clearable>
            <label>{{ $t("procurement.filters.sku") }}</label>
            <md-input v-model="filters.sku" @input="debounceApplyFilters"></md-input>
          </md-field>
          <md-switch v-model="filters.exactMatchSku" @change="debounceApplyFilters">{{ $t("procurement.filters.exactMatch") }}</md-switch>
        </div>
      </div>
      <div class="sp-dock-search">
        <a class="sp-dock-trigger" :class="{ 'sp-dock-trigger--active': supplierFilter }" @click="toggleDockFilter">
          <md-icon>filter_alt</md-icon>
        </a>
        <div v-show="dockFilterOpen" class="sp-dock-search-panel">
          <label class="dock-filter-label">{{ $t("procurement.filters.supplier") }}</label>
          <div class="wrap-v-select supplier-filter">
            <v-select
              class="custom-select"
              label="name"
              v-model="supplierFilter"
              :options="supplierNameOptions"
              :filter="filterSupplier"
              :placeholder="$t('procurement.filters.supplier')"
              @input="applyFilters({})"
            ></v-select>
          </div>
        </div>
      </div>
    </div>

    <!-- Sticky-Zusammenfassung unten rechts: EOL-Schalter + Warenkorb + Auswahl-Kennzahlen -->
    <div class="sp-dock-info procurement-dock-info">
      <md-switch v-model="filters.includeEol" @change="debounceApplyFilters" class="md-primary">{{ $t("procurement.filters.includeEol") }}</md-switch>

      <!-- Warenkorb-Positionen: entfernen geht sonst nur ueber die Zeile, die der Filter ausblenden kann -->
      <template v-if="cartOpen && selectedCount">
        <div class="cart-head">
          <strong>{{ $t("procurement.cart.title") }} · {{ selectedCount }}</strong>
          <a class="cart-clear" @click="clearCart">{{ $t("procurement.cart.clearAll") }}</a>
        </div>
        <div class="cart-list">
          <div v-for="item in cartItems" :key="item.productId" class="cart-row" :class="{ 'cart-row--foreign': isForeignSupplier(item) }">
            <span class="cart-sku" :title="item.supplierName">
              {{ item.sku }}
              <md-icon v-if="isForeignSupplier(item)" class="cart-warn-icon">warning</md-icon>
            </span>
            <input type="number" min="0" step="1" class="cart-qty" :value="item.orderQuantity" @input="updateCartQuantity(item, $event.target.value)" />
            <span class="cart-cbm">{{ calcCbm(item) | formatUnit("cbm", 2, 2) }}</span>
            <a class="cart-remove" :title="$t('procurement.cart.remove')" @click="removeFromCart(item)"><md-icon>close</md-icon></a>
          </div>
        </div>
        <div v-if="cartSupplierCount > 1" class="cart-hint">
          <md-icon>warning</md-icon>
          <span>{{ $t("procurement.cart.multiSupplier", { count: cartSupplierCount }) }}</span>
        </div>
      </template>

      <div class="sp-dock-info-row">
        <span>{{ $t("procurement.summary.sku") }}</span>
        <strong>{{ selectedCount }}</strong>
      </div>
      <div class="sp-dock-info-row">
        <span>{{ $t("procurement.summary.cbm") }}</span>
        <strong>{{ selectedCbm | formatUnit("cbm", 2, 2) }}</strong>
      </div>
      <div class="sp-dock-info-row">
        <span>{{ $t("procurement.summary.container") }} (40 HC)</span>
        <strong>{{ container40hc | formatUnit("", 2, 2) }}</strong>
      </div>
      <div class="sp-dock-info-row">
        <span>{{ $t("procurement.summary.container") }} (40 GP)</span>
        <strong>{{ container40gp | formatUnit("", 2, 2) }}</strong>
      </div>
      <div class="sp-dock-info-row">
        <span>{{ $t("procurement.summary.container") }} (20 GP)</span>
        <strong>{{ container20gp | formatUnit("", 2, 2) }}</strong>
      </div>
      <a v-if="selectedCount" class="cart-toggle" @click="cartOpen = !cartOpen">
        <md-icon>{{ cartOpen ? "expand_more" : "expand_less" }}</md-icon>
        <span>{{ cartOpen ? $t("procurement.cart.hide") : $t("procurement.cart.show") }}</span>
      </a>
    </div>

    <!-- Chart-Lightbox -->
    <modal v-if="chartModal" @close="chartModal = false" class-name="procurement-chart-modal">
      <template slot="header">
        <h4 class="modal-title">{{ chartRow && chartRow.sku }} – {{ $t("procurement.chart.title") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="chartModal = false"><md-icon>clear</md-icon></md-button>
      </template>
      <template slot="body">
        <div class="procurement-chart-wrap">
          <div v-if="chartRow" class="chart-legend">
            <span class="legend-item"><span class="legend-dot" style="background: #4caf50"></span>{{ $t("procurement.salesSplit.warehouse") }}</span>
            <span class="legend-item"><span class="legend-dot" style="background: #ff9800"></span>FBA</span>
          </div>
          <svg v-if="chartRow" :width="chartWidth" :height="chartHeight" class="big-chart">
            <g v-for="(bar, i) in bigChartBars" :key="i">
              <title>{{ bar.label }}: {{ bar.tooltip }}</title>
              <rect v-if="bar.hLager > 0" :x="bar.x" :y="bar.yLager" :width="bar.w" :height="bar.hLager" rx="2" fill="#4caf50"></rect>
              <rect v-if="bar.hFba > 0" :x="bar.x" :y="bar.yFba" :width="bar.w" :height="bar.hFba" rx="2" fill="#ff9800"></rect>
              <rect v-if="bar.total === 0" :x="bar.x" :y="yBase - 2" :width="bar.w" height="2" rx="1" fill="#e0e0e0"></rect>
              <text :x="bar.x + bar.w / 2" :y="bar.yTop - 6" text-anchor="middle" class="bar-value">{{ bar.total }}</text>
              <text :x="bar.x + bar.w / 2" :y="chartHeight - 6" text-anchor="middle" class="bar-label">{{ bar.label }}</text>
            </g>
          </svg>
        </div>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="chartModal = false">{{ $t("procurement.close") }}</md-button>
      </template>
    </modal>

    <!-- EOL/MOQ-Lightbox: schreibt direkt in Products -->
    <modal v-if="eolMoqModal" @close="closeEolMoqModal" class-name="procurement-eolmoq-modal">
      <template slot="header">
        <h4 class="modal-title">{{ eolMoqRow && eolMoqRow.sku }} – {{ $t("procurement.eolMoq.title") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="closeEolMoqModal"><md-icon>clear</md-icon></md-button>
      </template>
      <template slot="body">
        <div class="eolmoq-form">
          <md-field>
            <label>{{ $t("procurement.eolMoq.moq") }}</label>
            <md-input v-model="eolMoqForm.moq" type="number" min="0" step="1"></md-input>
          </md-field>
          <md-switch v-model="eolMoqForm.eol" class="md-primary">{{ $t("procurement.eolMoq.eol") }}</md-switch>
        </div>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="closeEolMoqModal">{{ $t("procurement.cancel") }}</md-button>
        <md-button class="md-success" :disabled="eolMoqSaving" @click="saveEolMoq">{{ $t("procurement.save") }}</md-button>
      </template>
    </modal>

    <!-- Hinweis: mehrere Lieferanten -->
    <modal v-if="multiSupplierModal" @close="multiSupplierModal = false">
      <template slot="header">
        <h4 class="modal-title">{{ $t("procurement.order.multiSupplierTitle") }}</h4>
      </template>
      <template slot="body">
        <p>{{ $t("procurement.order.multiSupplierText") }}</p>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="multiSupplierModal = false">{{ $t("procurement.close") }}</md-button>
      </template>
    </modal>

    <!-- Bestell-Bestätigung -->
    <modal v-if="confirmModal" @close="confirmModal = false">
      <template slot="header">
        <h4 class="modal-title">{{ $t("procurement.order.confirmTitle") }}</h4>
      </template>
      <template slot="body">
        <p>{{ $t("procurement.order.confirmText", { supplier: orderSupplierName }) }}</p>
        <div class="order-options">
          <md-checkbox v-model="orderCreatePlan">{{ $t("procurement.order.createShippingPlan") }}</md-checkbox>
          <md-checkbox v-model="orderSendEmail">{{ $t("procurement.order.sendEmail") }}</md-checkbox>
        </div>
        <p v-if="orderSendEmail && !orderSupplierEmail" class="text-danger">{{ $t("procurement.order.noSupplierEmail") }}</p>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="confirmModal = false">{{ $t("procurement.cancel") }}</md-button>
        <md-button
          class="md-success"
          v-if="hasPermission('Write', 'Procurement', 'Order')"
          :disabled="(!orderCreatePlan && !orderSendEmail) || (orderSendEmail && !orderSupplierEmail)"
          @click="confirmOrder"
        >
          {{ $t("procurement.confirm") }}
        </md-button>
      </template>
    </modal>

    <!-- E-Mail-Editor -->
    <modal v-if="emailModal" @close="emailModal = false" class-name="procurement-email-modal">
      <template slot="header">
        <h4 class="modal-title">{{ $t("procurement.email.title", { supplier: orderSupplierName }) }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="emailModal = false"><md-icon>clear</md-icon></md-button>
      </template>
      <template slot="body">
        <md-field>
          <label>{{ $t("procurement.email.to") }}</label>
          <md-input :value="orderSupplierEmail" readonly></md-input>
        </md-field>
        <md-field>
          <label>{{ $t("procurement.email.subject") }}</label>
          <md-input v-model="emailSubject"></md-input>
        </md-field>
        <md-field>
          <label>{{ $t("procurement.email.body") }}</label>
          <md-textarea v-model="emailBody" class="email-body-input"></md-textarea>
        </md-field>
      </template>
      <template slot="footer">
        <md-button class="md-simple" @click="emailModal = false">{{ $t("procurement.cancel") }}</md-button>
        <md-button class="md-success" v-if="hasPermission('Write', 'Procurement', 'Order')" @click="submitOrder">{{ $t("procurement.email.send") }}</md-button>
      </template>
    </modal>
  </div>
</template>
<script>
import { debounce } from "lodash";
import { Modal } from "@/components";
import axiosFactory from "../../services/axios.factory";
import CSLoading from "../../components/Loading/CSLoading.vue";
import { EventBus } from "@/helpers/event-bus";
import SalesSparkline from "./SalesSparkline.vue";
import { hasPermission } from "../../mixins/auth";

export default {
  name: "Procurement",
  components: {
    Modal,
    CSLoading,
    SalesSparkline,
  },
  data() {
    return {
      isLoading: true,
      currentPage: 1,
      filters: {
        keywords: "",
        sku: "",
        exactMatchSku: false,
        includeEol: false,
      },
      supplierFilter: null,
      dockSearchOpen: false,
      dockFilterOpen: false,
      selected: {},
      cartOpen: false,
      chartModal: false,
      chartRow: null,
      chartWidth: 880,
      chartHeight: 400,
      multiSupplierModal: false,
      eolMoqModal: false,
      eolMoqRow: null,
      eolMoqForm: { moq: null, eol: false },
      eolMoqSaving: false,
      confirmModal: false,
      emailModal: false,
      orderCreatePlan: true,
      orderSendEmail: true,
      orderSupplier: null,
      emailSubject: "",
      emailBody: "",
      tableColumns: ["select", "priority", "sku", "stock", "sales", "chart", "range", "moq", "supplierName", "prices", "recommendation"],
      tableOptions: {
        headings: {
          select: () => "",
          priority: () => this.$t("procurement.table.priority"),
          sku: () => this.$t("procurement.table.sku"),
          stock: () => this.$t("procurement.table.stock"),
          sales: () => this.$t("procurement.table.sales"),
          chart: () => this.$t("procurement.table.chart"),
          range: () => this.$t("procurement.table.range"),
          moq: () => this.$t("procurement.table.moq"),
          supplierName: () => this.$t("procurement.table.supplier"),
          prices: () => this.$t("procurement.table.prices"),
          recommendation: () => this.$t("procurement.table.recommendation"),
        },
        perPage: 25,
        perPageValues: [25, 100, 500],
        filterable: false,
        sortIcon: {
          is: "fa-sort",
          base: "fa",
          up: "fa-sort-up",
          down: "fa-sort-down",
        },
        sortable: ["priority", "sku", "stock", "sales", "range", "moq", "supplierName", "recommendation"],
        params: {},
        resizableColumns: false,
        requestFunction: this.requestFunction,
        responseAdapter: this.responseFunction,
        pagination: {
          show: true,
        },
        texts: {
          count: this.$t("table.count"),
          limit: this.$t("table.records"),
          noResults: this.$t("table.noRecord"),
        },
        columnsClasses: {
          sku: "text-left",
          stock: "text-left",
          sales: "text-left",
          range: "text-left",
          supplierName: "text-left",
          recommendation: "text-left",
        },
      },
      tableData: [],
      totalItems: 0,
    };
  },
  computed: {
    supplierNameOptions() {
      return (this.$store.state.shippingData.supplierNames || []).map((s) => ({ value: s.id, name: s.name, nameFull: s.nameFull, creditor: s.creditor, bankId: s.bankId }));
    },
    selectedItems() {
      return Object.values(this.selected);
    },
    selectedCount() {
      return this.selectedItems.length;
    },
    // Summe der CBM über die ausgewählten Zeilen (jeweils mit ihrer eingegebenen Menge)
    selectedCbm() {
      return this.selectedItems.reduce((sum, item) => {
        const cbm = this.calcCbm(item);
        return sum + (cbm || 0);
      }, 0);
    },
    // Warenkorb-Positionen, stabil sortiert nach SKU
    cartItems() {
      return this.selectedItems.slice().sort((a, b) => String(a.sku || "").localeCompare(String(b.sku || "")));
    },
    // Bestellt wird immer bei genau einem Lieferanten -> mehrere im Korb sind ein Hinweis wert
    cartSupplierCount() {
      return new Set(this.selectedItems.map((i) => i.supplierId)).size;
    },
    // Lieferant der Mehrheit; alle anderen Positionen werden im Korb markiert
    cartMainSupplierId() {
      const counts = {};
      this.selectedItems.forEach((i) => {
        counts[i.supplierId] = (counts[i.supplierId] || 0) + 1;
      });
      let best = null;
      Object.keys(counts).forEach((id) => {
        if (best === null || counts[id] > counts[best]) best = id;
      });
      return best === null ? null : best;
    },
    // Container-Auslastung: CBM geteilt durch die Ladevolumen-Teiler
    container40hc() {
      return this.selectedCbm / 76;
    },
    container40gp() {
      return this.selectedCbm / 66;
    },
    container20gp() {
      return this.selectedCbm / 33;
    },
    allOnPageSelected: {
      get() {
        return this.tableData.length > 0 && this.tableData.every((r) => this.selected[r.productId]);
      },
      set(value) {
        this.tableData.forEach((r) => {
          r.checked = value;
          this.toggleCart(r, value);
        });
      },
    },
    orderSupplierName() {
      return this.orderSupplier && this.orderSupplier.supplierName;
    },
    // Basislinie des grossen Charts (fuer die Null-Monats-Markierung)
    yBase() {
      return this.chartHeight - 30;
    },
    orderSupplierEmail() {
      return this.orderSupplier && this.orderSupplier.supplierEmail;
    },
    bigChartBars() {
      if (!this.chartRow) return [];
      const series = this.chartRow.monthlySales || [];
      const n = series.length || 1;
      const padding = 20;
      const innerWidth = this.chartWidth - 2 * padding;
      const barSpace = innerWidth / n;
      const barWidth = Math.floor(barSpace * 0.6);
      // Stapel: Lager (gruen) unten + FBA (orange) oben; Skala nach dem staerksten Gesamtmonat
      const maxVal = Math.max(1, ...series.map((s) => (s.quantity || 0) + (s.quantityFba || 0)));
      const chartInner = this.chartHeight - 60;
      const baseY = this.chartHeight - 30;
      return series.map((s, i) => {
        const lager = s.quantity || 0;
        const fba = s.quantityFba || 0;
        const total = lager + fba;
        const hLager = lager > 0 ? Math.max(2, Math.round((chartInner * lager) / maxVal)) : 0;
        const hFba = fba > 0 ? Math.max(2, Math.round((chartInner * fba) / maxVal)) : 0;
        const d = new Date(s.month);
        return {
          x: padding + i * barSpace + (barSpace - barWidth) / 2,
          w: barWidth,
          yLager: baseY - hLager,
          hLager,
          yFba: baseY - hLager - hFba,
          hFba,
          yTop: baseY - Math.max(2, hLager + hFba),
          lager,
          fba,
          total,
          label: `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(2)}`,
          tooltip: `${this.$t("procurement.salesSplit.warehouse")} ${lager} · FBA ${fba} · ${this.$t("procurement.salesSplit.total")} ${total}`,
        };
      });
    },
  },
  methods: {
    hasPermission(permissionName, tableName, columnName) {
      return hasPermission(permissionName, tableName, columnName);
    },
    onOrderClick() {
      if (this.selectedCount > 0) this.startOrder();
    },
    toggleDockSearch() {
      this.dockSearchOpen = !this.dockSearchOpen;
      if (this.dockSearchOpen) {
        this.dockFilterOpen = false;
        this.$nextTick(() => {
          const ref = this.$refs.dockSearchInput;
          const input = ref && ref.$el && ref.$el.querySelector("input");
          if (input) input.focus();
        });
      }
    },
    toggleDockFilter() {
      this.dockFilterOpen = !this.dockFilterOpen;
      if (this.dockFilterOpen) this.dockSearchOpen = false;
    },
    openEolMoqModal(row) {
      this.eolMoqRow = row;
      this.eolMoqForm = { moq: row.moq != null ? row.moq : null, eol: !!row.eol };
      this.eolMoqModal = true;
    },
    closeEolMoqModal() {
      this.eolMoqModal = false;
      this.eolMoqRow = null;
    },
    saveEolMoq() {
      if (!this.eolMoqRow) return;
      const moqRaw = this.eolMoqForm.moq;
      const payload = {
        moq: moqRaw === "" || moqRaw === null || moqRaw === undefined ? null : parseInt(moqRaw, 10),
        eol: !!this.eolMoqForm.eol,
      };
      this.eolMoqSaving = true;
      axiosFactory()
        .put(`/procurement/items/${this.eolMoqRow.productId}/eol_moq`, payload)
        .then(() => {
          this.$notify({
            timeout: 2500,
            message: this.$t("procurement.eolMoq.updated"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
          this.eolMoqModal = false;
          this.eolMoqRow = null;
          if (this.$refs.table) this.$refs.table.refresh();
        })
        .catch(() => {
          this.$notify({
            timeout: 3000,
            message: this.$t("procurement.eolMoq.error"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "danger",
          });
        })
        .finally(() => {
          this.eolMoqSaving = false;
        });
    },
    filterSupplier(options, search) {
      const s = (search || "").toLowerCase().trim();
      if (!s) return options;
      return options.filter((o) => [o.name, o.nameFull, o.creditor, o.bankId].some((v) => v != null && String(v).toLowerCase().includes(s)));
    },
    requestFunction(data) {
      const { page, limit, orderBy, ascending } = data;
      this.currentPage = page;
      const params = {
        pageIndex: this.currentPage,
        pageSize: limit,
        orderBy,
        ascending,
        keywords: this.filters.keywords || undefined,
        includeEol: this.filters.includeEol,
        supplierId: this.supplierFilter ? this.supplierFilter.value : undefined,
      };
      if (this.filters.sku) {
        params.sku = this.filters.sku;
        params.exactMatchSku = this.filters.exactMatchSku;
      }
      // Gruppenspalten auf das Sortier-Feld des Backends mappen
      if (orderBy === "stock") params.orderBy = "totalStock";
      if (orderBy === "sales") params.orderBy = "sales365";
      if (orderBy === "range") params.orderBy = "range365";
      if (orderBy === "recommendation") params.orderBy = "recommendedQuantity";
      this.isLoading = true;
      return axiosFactory()
        .get(`/procurement/items`, { params })
        .catch(function (e) {})
        .finally(() => {
          this.isLoading = false;
        });
    },
    responseFunction({ data }) {
      this.tableData = data.data.map((i) => ({
        ...i,
        checked: !!this.selected[i.productId],
        orderQuantity: this.selected[i.productId] ? this.selected[i.productId].orderQuantity : i.recommendedQuantity || null,
      }));
      this.totalItems = data.total;
      return {
        data: this.tableData,
        count: data.total,
      };
    },
    debounceApplyFilters: debounce(function () {
      this.applyFilters({});
    }, 300),
    applyFilters(filters) {
      this.$refs.table.options.params = {
        ...this.$refs.table.options.params,
        ...filters,
        pageIndex: 0,
      };
      this.currentPage = 1;
      this.$refs.table.setPage(this.currentPage);
    },
    toggleCart(row, checked) {
      if (checked) {
        // Kopie ablegen: die Tabellenzeile wird beim naechsten Laden neu aufgebaut
        this.$set(this.selected, row.productId, { ...row, orderQuantity: row.orderQuantity });
      } else {
        this.$delete(this.selected, row.productId);
      }
    },
    removeFromCart(item) {
      this.$delete(this.selected, item.productId);
      const row = this.tableData.find((r) => r.productId === item.productId);
      if (row) row.checked = false;
    },
    clearCart() {
      this.selected = {};
      this.tableData.forEach((r) => {
        r.checked = false;
      });
    },
    updateCartQuantity(item, value) {
      const qty = value === "" ? null : Number(value);
      this.$set(this.selected, item.productId, { ...this.selected[item.productId], orderQuantity: qty });
      const row = this.tableData.find((r) => r.productId === item.productId);
      if (row) row.orderQuantity = qty;
    },
    // Mengenaenderung in der Tabelle in den Warenkorb spiegeln (dort liegt eine Kopie der Zeile)
    syncRowQuantity(row) {
      if (!this.selected[row.productId]) return;
      this.$set(this.selected, row.productId, { ...this.selected[row.productId], orderQuantity: row.orderQuantity });
    },
    isForeignSupplier(item) {
      return this.cartSupplierCount > 1 && String(item.supplierId) !== String(this.cartMainSupplierId);
    },
    salesTooltip(row, days) {
      const lager = row["sales" + days] || 0;
      const fba = row["fba" + days] || 0;
      return `${this.$t("procurement.salesSplit.warehouse")}: ${lager} · FBA: ${fba} · ${this.$t("procurement.salesSplit.total")}: ${lager + fba}`;
    },
    priorityClass(priority) {
      if (!priority) return "priority-none";
      return `priority-${priority}`;
    },
    formatRange(value) {
      if (value == null) return "–";
      return this.$options.filters.formatUnit(value, this.$t("procurement.months"), 1, 1);
    },
    calcCbm(row) {
      const qty = row.orderQuantity;
      if (!qty || !row.shippingSizeLength || !row.shippingSizeWidth || !row.shippingSizeHeight) return null;
      return Math.round((qty * row.shippingSizeLength * row.shippingSizeWidth * row.shippingSizeHeight) / 10000) / 100;
    },
    // VK ist brutto (19 %); netto = VK / 1,19
    nettoVk(row) {
      if (row.currentSellingPrice == null) return null;
      return row.currentSellingPrice / 1.19;
    },
    // Marge in EUR = Netto-VK - BEP
    marginEur(row) {
      const netto = this.nettoVk(row);
      if (netto == null || row.bep == null) return null;
      return netto - row.bep;
    },
    // Marge in % = Marge / Netto-VK * 100
    marginPct(row) {
      const netto = this.nettoVk(row);
      const marge = this.marginEur(row);
      if (netto == null || marge == null || netto === 0) return null;
      return (marge / netto) * 100;
    },
    margeClass(row) {
      const m = this.marginEur(row);
      if (m == null) return "";
      return m >= 0 ? "marge-positive" : "marge-negative";
    },
    // Ampel fuer Marge %: unter 30 rot, 30-40 orange, ueber 40 gruen
    margePctClass(row) {
      const pct = this.marginPct(row);
      if (pct == null) return "";
      if (pct < 30) return "marge-negative";
      if (pct > 40) return "marge-positive";
      return "marge-warn";
    },
    filterBySupplier(row) {
      this.supplierFilter = { value: row.supplierId, name: row.supplierName };
      this.applyFilters({});
    },
    showChartModal(row) {
      this.chartRow = row;
      this.chartModal = true;
    },
    startOrder() {
      const items = this.selectedItems;
      if (items.length === 0) return;
      const supplierIds = [...new Set(items.map((i) => i.supplierId))];
      if (supplierIds.length > 1) {
        this.multiSupplierModal = true;
        return;
      }
      this.orderSupplier = items[0];
      this.orderCreatePlan = true;
      this.orderSendEmail = true;
      this.confirmModal = true;
    },
    confirmOrder() {
      this.confirmModal = false;
      if (this.orderSendEmail) {
        this.prepareEmail();
        this.emailModal = true;
      } else {
        this.submitOrder();
      }
    },
    prepareEmail() {
      const items = this.selectedItems;
      const today = new Date();
      const dateStr = `${String(today.getDate()).padStart(2, "0")}.${String(today.getMonth() + 1).padStart(2, "0")}.${today.getFullYear()}`;
      this.emailSubject = `Purchase Order CS Trading – ${dateStr}`;
      const contact = this.orderSupplier.supplierContact;
      const salutation = contact ? `Dear ${contact},` : "Dear Sir or Madam,";
      const lines = items.map((i) => `${i.orderQuantity || 0} x ${i.sku}`);
      this.emailBody = `${salutation}

we would like to place the following order:

${lines.join("\n")}

Please confirm receipt of this order and let us know the expected production and delivery time.

Best regards
CS Trading GmbH & Co. KG`;
    },
    submitOrder() {
      const items = this.selectedItems;
      const invalid = items.filter((i) => !i.orderQuantity || i.orderQuantity <= 0);
      if (invalid.length > 0) {
        this.$notify({
          timeout: 3500,
          message: this.$t("procurement.order.invalidQuantities", { skus: invalid.map((i) => i.sku).join(", ") }),
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return;
      }
      const payload = {
        supplierId: this.orderSupplier.supplierId,
        items: items.map((i) => ({ productId: i.productId, quantity: i.orderQuantity })),
        createShippingPlan: this.orderCreatePlan,
        sendEmail: this.orderSendEmail,
        emailSubject: this.emailSubject,
        emailBody: this.emailBody,
      };
      this.isLoading = true;
      axiosFactory()
        .post(`/procurement/order`, payload)
        .then((res) => {
          const result = res.data.data;
          if (result.error) {
            this.$notify({
              timeout: 4000,
              message: this.$t(`procurement.order.errors.${result.error}`),
              icon: "add_alert",
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "danger",
            });
            return;
          }
          this.emailModal = false;
          this.$notify({
            timeout: 3000,
            message: this.orderCreatePlan
              ? this.$t("procurement.order.successWithPlan", { supplier: this.orderSupplierName })
              : this.$t("procurement.order.success", { supplier: this.orderSupplierName }),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
          this.selected = {};
          this.$refs.table.refresh();
        })
        .catch(() => {
          this.$notify({
            timeout: 3500,
            message: this.$t("procurement.order.errors.request_failed"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "danger",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  updated() {
    EventBus.$emit("scroll:changed");
  },
  created() {
    this.$store.dispatch("shippingData/getSupplierNames");
  },
};
</script>
<style lang="scss">
.table-procurement {
  td {
    font-size: 12px;
    padding: 4px 6px !important;
    vertical-align: middle;
  }
}

.cell-moq {
  display: flex;
  align-items: center;
  gap: 4px;

  .moq-eol-info {
    line-height: 1.3;
  }

  .eol-indicator {
    display: flex;
    align-items: center;
    gap: 2px;

    .md-icon {
      font-size: 16px !important;
      width: 16px;
      min-width: 16px;
      height: 16px;
    }
    .eol-yes {
      color: #ff9800 !important;
    }
    .eol-no {
      color: #4caf50 !important;
    }
  }

  .eol-moq-edit {
    margin: 0;
    width: 26px;
    min-width: 26px;
    height: 26px;

    .md-icon {
      font-size: 16px !important;
    }
  }
}

.eolmoq-form {
  min-width: 260px;
  padding: 4px 2px;
}

.cell-prices {
  line-height: 1.35;
  white-space: nowrap;

  .marge-positive {
    color: #4caf50;
    font-weight: 600;
  }
  .marge-negative {
    color: #f44336;
    font-weight: 600;
  }
  .marge-warn {
    color: #ff9800;
    font-weight: 600;
  }
}

/* Sticky-Dock rechts (nach Lieferplan-Muster) */
.sp-dock {
  position: fixed;
  top: 110px;
  right: 0;
  z-index: 1031;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.sp-dock-trigger {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid #bbb;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
}
.sp-dock-trigger .md-icon {
  color: #fff !important;
}
.sp-dock-trigger--active {
  background: #4caf50;
}
.sp-dock-trigger--disabled {
  opacity: 0.45;
  cursor: default;
}
.sp-dock-badge {
  position: absolute;
  top: -4px;
  left: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: #4caf50;
  color: #fff;
  border-radius: 9px;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
}
.sp-dock-search {
  position: relative;
}
.sp-dock-search-panel {
  position: absolute;
  right: 44px;
  top: 0;
  width: 320px;
  background: #fff;
  padding: 20px 16px 16px;
  border-radius: 6px 0 0 6px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.25);
  overflow: visible;
}
.sp-dock-search-panel .md-field {
  margin-bottom: 8px;
}
.sp-dock-search-panel .dock-filter-label {
  display: block;
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
}

.sp-dock-info.procurement-dock-info {
  position: fixed;
  bottom: 12px;
  right: 0;
  z-index: 1029;
  background: #fff;
  border: 1px solid #ddd;
  border-right: 0 none;
  border-radius: 8px 0 0 8px;
  padding: 4px 12px 8px;
  font-size: 12px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 210px;
  max-width: 320px;
  overflow-x: hidden;
}
.procurement-dock-info .cart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0 6px;
  border-bottom: 1px solid #eee;
}
.procurement-dock-info .cart-clear {
  color: #f44336;
  cursor: pointer;
}
.procurement-dock-info .cart-list {
  max-height: 220px;
  overflow-y: auto;
  overflow-x: hidden;
}
.procurement-dock-info .cart-row {
  display: grid;
  /* minmax(0, 1fr): ohne die 0-Untergrenze schiebt eine lange SKU die Zeile breiter
     als die Box und erzeugt einen Querscrollbalken. */
  grid-template-columns: minmax(0, 1fr) 52px 62px 18px;
  gap: 6px;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}
.procurement-dock-info .cart-row--foreign {
  background: #fff8e1;
}
.procurement-dock-info .cart-row--foreign .cart-sku,
.procurement-dock-info .cart-row--foreign .cart-cbm {
  color: #ef6c00;
}
.procurement-dock-info .cart-sku {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.procurement-dock-info .cart-warn-icon {
  font-size: 13px !important;
  color: #ef6c00 !important;
  vertical-align: middle;
}
.procurement-dock-info .cart-qty {
  width: 100%;
  height: 22px;
  font-size: 12px;
  text-align: right;
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 0 4px;
}
.procurement-dock-info .cart-cbm {
  text-align: right;
  color: #666;
}
.procurement-dock-info .cart-remove {
  cursor: pointer;
  line-height: 1;
}
.procurement-dock-info .cart-remove .md-icon {
  font-size: 15px !important;
  color: #999 !important;
}
.procurement-dock-info .cart-remove:hover .md-icon {
  color: #f44336 !important;
}
.procurement-dock-info .cart-hint {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  background: #fff8e1;
  color: #ef6c00;
  border-radius: 4px;
  padding: 4px 6px;
  margin: 6px 0;
}
.procurement-dock-info .cart-hint .md-icon {
  font-size: 14px !important;
  color: #ef6c00 !important;
}
.procurement-dock-info .cart-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-top: 1px solid #eee;
  margin-top: 6px;
  padding-top: 6px;
  color: #4caf50;
  cursor: pointer;
}
.procurement-dock-info .cart-toggle .md-icon {
  font-size: 16px !important;
  color: #4caf50 !important;
}
.procurement-dock-info .md-switch {
  margin: 6px 0;
}
.procurement-dock-info .sp-dock-info-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 1px 0;
}
.procurement-filter-row {
  align-items: center;

  .supplier-filter {
    margin-top: 10px;
  }

  .order-button-wrap {
    text-align: right;
  }
}

.cell-priority {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.priority-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex: 0 0 14px;

  &.priority-1 {
    background-color: #69f0ae;
    animation: priority-pulse 1.2s ease-in-out infinite;
  }
  &.priority-2 {
    background-color: #4caf50;
  }
  &.priority-3 {
    background-color: #f9c800;
  }
  &.priority-4 {
    background-color: #ff9800;
  }
  &.priority-5 {
    background-color: #f44336;
  }
  &.priority-none {
    background-color: #bdbdbd;
  }
}

@keyframes priority-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(105, 240, 174, 0.7);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(105, 240, 174, 0);
  }
}

.cell-grouped {
  line-height: 1.35;
}

.supplier-link {
  color: #4caf50 !important;
  text-decoration: underline;
  cursor: pointer;
}

.cell-recommendation {
  .cbm-info {
    color: #999;
    font-size: 11px;
    white-space: nowrap;
  }

  .order-quantity-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .order-quantity-field {
    width: 70px;
    min-height: unset;
    margin: 0;
    padding-top: 0;

    input {
      font-size: 12px;
      height: 24px;
    }
  }
}

.row-select-checkbox {
  margin: 0;
}

.modal-container.procurement-chart-modal {
  width: min(960px, 95vw) !important;
  max-width: min(960px, 95vw) !important;
}

.procurement-chart-wrap {
  overflow-x: auto;
}

.big-chart {
  display: block;
  margin: 0 auto;

  .bar-value {
    font-size: 12px;
    fill: #333;
  }

  .bar-label {
    font-size: 11px;
    fill: #777;
  }
}

.chart-legend {
  display: flex;
  justify-content: center;
  gap: 16px;
  font-size: 12px;
  color: #555;

  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    display: inline-block;
  }
}

.modal-container.procurement-email-modal {
  width: min(700px, 95vw) !important;
  max-width: min(700px, 95vw) !important;
}

.email-body-input {
  min-height: 260px !important;
  font-family: inherit;
}

.order-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
